import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["navItem", "iconContainer", "icon", "label"];

  // Utility function for default path matching
  pathMatcher = (basePath) => {
    return (currentPath) => {
      if (basePath === "/") {
        return currentPath === "/";
      }
      // Match exact path or any subpath
      return currentPath === basePath || currentPath.startsWith(`${basePath}/`);
    };
  };

  // Route configuration with customizable matchers
  ROUTE_CONFIG = {
    home: {
      matcher: this.pathMatcher("/"),
    },
    listings: {
      matcher: this.pathMatcher("/listings"),
    },
    contacts: {
      matcher: this.pathMatcher("/contacts"),
    },
    tools: {
      matcher: this.pathMatcher("/tools"),
    },
    calendar: {
      matcher: this.pathMatcher("/calendar"),
    },
    profile: {
      matcher: this.pathMatcher("/profile"),
    },
  };

  connect() {
    this.isInvertedNav = false; // Track if nav should be inverted (white bg/black text)
    this.updateActiveState();

    // Watch the nav wrapper element for changes
    const navWrapper = document.getElementById('bottom-nav-wrapper');
    if (!navWrapper) return;
    
    // Listen for custom nav update events from layout controller
    navWrapper.addEventListener("nav:updated", (event) => {
      this.updateActiveState();
    });
    
    // Listen for theme changes
    document.addEventListener("theme:changed", (event) => {
      this.handleThemeChange(event.detail);
    });

    // Watch for data-active-nav attribute changes
    const observer = new MutationObserver(() => {
      this.updateActiveState();
    });
    
    observer.observe(navWrapper, { 
      attributes: true, 
      attributeFilter: ['data-active-nav'] 
    });
  }


  getActiveNavKey() {
    // Check for view-specific override first
    const navWrapper = document.getElementById('bottom-nav-wrapper');
    const viewOverride = navWrapper?.dataset.activeNav;

    if (viewOverride) {
      return viewOverride;
    }

    // Match current path to nav key using matcher functions
    const currentPath = window.location.pathname;

    // Find which nav key matches this path
    for (const [navKey, config] of Object.entries(this.ROUTE_CONFIG)) {
      if (!config.matcher(currentPath)) {
        continue;
      }

      return navKey;
    }

    // Default to home
    return "home";
  }

  handleThemeChange({ isDark: isLayoutDark }) {
    // When layout theme is dark, nav is inverted (white bg/black text)
    this.isInvertedNav = isLayoutDark;
    this.updateNavBackground();
    this.updateActiveState();
  }
  
  updateNavBackground() {
    // Inverted nav = white background, normal nav = black background
    const [removeClass, addClass] = this.isInvertedNav 
      ? ["bg-black", "bg-white"] 
      : ["bg-white", "bg-black"];
    
    this.element.classList.remove(removeClass);
    this.element.classList.add(addClass);
  }

  updateActiveState() {
    const activeNavKey = this.getActiveNavKey();

    this.navItemTargets.forEach((item, index) => {
      const itemKey = item.dataset.navKey;
      const isActive = itemKey === activeNavKey;

      const iconContainer = this.iconContainerTargets[index];
      const icon = this.iconTargets[index];
      const label = this.labelTargets[index];

      if (!isActive) {
        this.setInactiveStyles(iconContainer, icon, label);
        return;
      }
      
      this.setActiveStyles(iconContainer, icon, label);
    });
  }
  
  setInactiveStyles(iconContainer, icon, label) {
    iconContainer.classList.remove("bg-white", "bg-black");
    
    if (this.isInvertedNav) {
      // Inverted nav = gray icons/text
      icon.classList.remove("text-white", "text-black");
      icon.classList.add("text-oakwyn-gray-medium");
      label.classList.remove("text-oakwyn-gold", "text-white", "text-black");
      label.classList.add("text-oakwyn-gray-medium");
    } else {
      // Normal nav = white icons/text
      icon.classList.remove("text-black", "text-oakwyn-gray-medium");
      icon.classList.add("text-white");
      label.classList.remove("text-oakwyn-gold", "text-oakwyn-gray-medium");
      label.classList.add("text-white");
    }
  }
  
  setActiveStyles(iconContainer, icon, label) {
    // Inverted nav (white bg) = black circle with white icon
    // Normal nav (black bg) = white circle with black icon
    const [bgColor, removeBg] = this.isInvertedNav 
      ? ["bg-black", "bg-white"] 
      : ["bg-white", "bg-black"];
    
    const [iconColor, removeIconColor] = this.isInvertedNav 
      ? ["text-white", "text-black"] 
      : ["text-black", "text-white"];
    
    iconContainer.classList.add(bgColor);
    iconContainer.classList.remove(removeBg);
    icon.classList.add(iconColor);
    icon.classList.remove(removeIconColor, "text-oakwyn-gray-medium");
    
    // Remove ALL possible text colors before adding gold
    label.classList.remove("text-white", "text-black", "text-oakwyn-gray-medium");
    label.classList.add("text-oakwyn-gold");
  }
}
